// ConfigAgente.js (MVP) – permite al cliente configurar su bot en minutos
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useOrganization } from '../context/OrganizationContext';

const DEFAULT_CFG = {
  nombreNegocio: '',
  rubro: '',
  descripcion: '',
  faqs: '',
  tono: 'cercano',
  politicas: '',
  enlacesClave: { catalogo: '', agendar: '', sitio: '' },
};

export default function ConfigAgente({ botId = 'default' }) {
  const { t } = useTranslation('bots');
  const { context } = useOrganization();
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      const user = auth.currentUser;
      if (!user) return setLoading(false);

      const basePath = context.mode === 'organization' && context.orgId
        ? `organizations/${context.orgId}/bots/${botId}`
        : `clientes/${user.uid}/bots/${botId}`;
      const ref = doc(db, basePath);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setCfg({
          nombreNegocio: data.nombreNegocio || '',
          rubro: data.rubro || '',
          descripcion: data.descripcion || '',
          faqs: Array.isArray(data.faqs) ? data.faqs.join('\n') : (data.faqs || ''),
          tono: data.tono || 'cercano',
          politicas: data.politicas || '',
          enlacesClave: {
            catalogo: data.enlacesClave?.catalogo || '',
            agendar: data.enlacesClave?.agendar || '',
            sitio: data.enlacesClave?.sitio || '',
          },
        });
      }
      setLoading(false);
    })();
  }, [botId, context]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaved(false);
    const user = auth.currentUser;
    if (!user) return;

    if (!cfg.nombreNegocio?.trim() || !cfg.rubro?.trim()) {
      alert(t('agentConfig.validationRequired'));
      return;
    }

    const savePath = context.mode === 'organization' && context.orgId
      ? `organizations/${context.orgId}/bots/${botId}`
      : `clientes/${user.uid}/bots/${botId}`;
    const ref = doc(db, savePath);
    await setDoc(ref, {
      nombreNegocio: cfg.nombreNegocio.trim(),
      rubro: cfg.rubro.trim(),
      descripcion: cfg.descripcion.trim(),
      faqs: cfg.faqs ? cfg.faqs.split('\n').map(s => s.trim()).filter(Boolean) : [],
      tono: cfg.tono,
      politicas: cfg.politicas.trim(),
      enlacesClave: {
        catalogo: (cfg.enlacesClave.catalogo || '').trim(),
        agendar: (cfg.enlacesClave.agendar || '').trim(),
        sitio: (cfg.enlacesClave.sitio || '').trim(),
      },
      actualizadoEn: new Date().toISOString(),
    }, { merge: true });

    setSaved(true);
  };

  if (loading) return <div>{t('agentConfig.loading')}</div>;

  return (
    <form onSubmit={onSave} style={{ display:'grid', gap:12, maxWidth:760 }}>
      <h3>{t('agentConfig.title')}</h3>

      <label>{t('agentConfig.businessName')}</label>
      <input value={cfg.nombreNegocio}
             onChange={e=>setCfg({...cfg, nombreNegocio:e.target.value})}
             placeholder={t('agentConfig.businessNamePlaceholder')} />

      <label>{t('agentConfig.industry')}</label>
      <input value={cfg.rubro}
             onChange={e=>setCfg({...cfg, rubro:e.target.value})}
             placeholder={t('agentConfig.industryPlaceholder')} />

      <label>{t('agentConfig.businessDescription')}</label>
      <textarea rows={3}
        value={cfg.descripcion}
        onChange={e=>setCfg({...cfg, descripcion:e.target.value})}
        placeholder={t('agentConfig.businessDescriptionPlaceholder')} />

      <label>{t('agentConfig.faqs')}</label>
      <textarea rows={4}
        value={cfg.faqs}
        onChange={e=>setCfg({...cfg, faqs:e.target.value})}
        placeholder={t('agentConfig.faqsPlaceholder')} />

      <label>{t('agentConfig.tone')}</label>
      <select value={cfg.tono} onChange={e=>setCfg({...cfg, tono:e.target.value})}>
        <option value="cercano">{t('agentConfig.toneFriendly')}</option>
        <option value="formal">{t('agentConfig.toneFormal')}</option>
        <option value="tecnico">{t('agentConfig.toneTechnical')}</option>
      </select>

      <label>{t('agentConfig.policies')}</label>
      <textarea rows={3}
        value={cfg.politicas}
        onChange={e=>setCfg({...cfg, politicas:e.target.value})}
        placeholder={t('agentConfig.policiesPlaceholder')} />

      <fieldset style={{ border:'1px solid #ddd', padding:12 }}>
        <legend>{t('agentConfig.keyLinks')}</legend>
        <input
          placeholder={t('agentConfig.linkCatalog')}
          value={cfg.enlacesClave.catalogo}
          onChange={e=>setCfg({...cfg, enlacesClave:{...cfg.enlacesClave, catalogo:e.target.value}})}
          style={{ marginBottom:8 }}
        />
        <input
          placeholder={t('agentConfig.linkBooking')}
          value={cfg.enlacesClave.agendar}
          onChange={e=>setCfg({...cfg, enlacesClave:{...cfg.enlacesClave, agendar:e.target.value}})}
          style={{ marginBottom:8 }}
        />
        <input
          placeholder={t('agentConfig.linkWebsite')}
          value={cfg.enlacesClave.sitio}
          onChange={e=>setCfg({...cfg, enlacesClave:{...cfg.enlacesClave, sitio:e.target.value}})}
        />
      </fieldset>

      <button type="submit">{t('agentConfig.save')}</button>
      {saved && <div style={{ color:'#2e7d32' }}>{t('agentConfig.saved')}</div>}

      <small style={{opacity:.7}}>
        Se guarda en <code>clientes/&lt;uid&gt;/bots/{'{'}botId{'}'}</code>.
      </small>
    </form>
  );
}